import React, { useState, useEffect } from 'react';
import { Box, Container, Card, TextField, Button, Typography, Snackbar, Alert, CircularProgress, Grid, RadioGroup, FormControlLabel, Radio, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { galleryService, campaignService } from '../services/api';
import { ArrowBack, Save, CloudUpload, Delete, Image, VideoLibrary, Add } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssetUrl } from '../config';

const GalleryEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [mediaInputType, setMediaInputType] = useState('file'); // 'url' or 'file'
  const [removedMediaIds, setRemovedMediaIds] = useState([]);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoUrls, setVideoUrls] = useState(['']);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    campaign_id: '',
    category: '',
    display_order: '0',
    media_items: []
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [galleryRes, campaignRes] = await Promise.all([
        galleryService.getGalleryById(id),
        campaignService.getAllCampaigns()
      ]);

      const galleryData = galleryRes.data?.data || galleryRes.data;
      const campaignData = campaignRes.data?.data || campaignRes.data || [];

      if (galleryData) {
        setFormData({
          title: galleryData.title || '',
          description: galleryData.description || '',
          campaign_id: galleryData.campaign_id || '',
          category: galleryData.category || '',
          display_order: galleryData.display_order || '0',
          media_items: galleryData.media || []
        });
      }

      setCampaigns(Array.isArray(campaignData) ? campaignData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setToast({ open: true, message: 'Error loading gallery item', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadFormData = new FormData();
      for (let i = 0; i < files.length; i++) {
        uploadFormData.append('images', files[i]);
      }

      const uploadResponse = await galleryService.uploadGalleryImages(uploadFormData);
      const uploadedFiles = uploadResponse.data?.data?.files || [];

      const newMediaItems = uploadedFiles.map((file, index) => ({
        media_type: 'image',
        media_url: file.path || file.image_url,
        filename: file.filename
      }));

      setFormData(prev => ({
        ...prev,
        media_items: [...prev.media_items, ...newMediaItems]
      }));

      setToast({ 
        open: true, 
        message: `${uploadedFiles.length} image(s) uploaded successfully!`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      setToast({
        open: true,
        message: 'Error uploading images: ' + (error.response?.data?.error || error.message),
        type: 'error'
      });
    }
  };

  const handleAddVideoUrl = (e) => {
    e.preventDefault();
    const videoUrl = prompt('Enter video URL (YouTube, Vimeo, etc.):');
    if (videoUrl && videoUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        media_items: [...prev.media_items, {
          media_type: 'video',
          media_url: videoUrl.trim()
        }]
      }));
      setToast({ open: true, message: 'Video URL added!', type: 'success' });
    }
  };

  const handleOpenVideoDialog = () => {
    setVideoUrls(['']);
    setVideoDialogOpen(true);
  };

  const handleCloseVideoDialog = () => {
    setVideoDialogOpen(false);
    setVideoUrls(['']);
  };

  const handleVideoUrlChange = (index, value) => {
    const updated = [...videoUrls];
    updated[index] = value;
    setVideoUrls(updated);
  };

  const handleAddVideoField = () => {
    setVideoUrls([...videoUrls, '']);
  };

  const handleRemoveVideoField = (index) => {
    setVideoUrls(videoUrls.filter((_, i) => i !== index));
  };

  const handleSubmitVideoUrls = () => {
    const validUrls = videoUrls.filter(url => url && url.trim());
    
    if (validUrls.length === 0) {
      setToast({ open: true, message: 'Please enter at least one video URL', type: 'error' });
      return;
    }

    const newMediaItems = validUrls.map(url => ({
      media_type: 'video',
      media_url: url.trim()
    }));

    setFormData(prev => ({
      ...prev,
      media_items: [...prev.media_items, ...newMediaItems]
    }));

    setToast({ 
      open: true, 
      message: `${validUrls.length} video URL(s) added successfully!`, 
      type: 'success' 
    });
    handleCloseVideoDialog();
  };

  const handleRemoveMedia = (index, mediaId) => {
    if (mediaId) {
      setRemovedMediaIds(prev => [...prev, mediaId]);
    }
    setFormData(prev => ({
      ...prev,
      media_items: prev.media_items.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      if (!formData.title) {
        setToast({ open: true, message: 'Title is required', type: 'error' });
        return;
      }

      if (formData.media_items.length === 0) {
        setToast({ open: true, message: 'Please keep at least one media item', type: 'error' });
        return;
      }

      setSaving(true);

      const galleryData = {
        title: formData.title,
        description: formData.description,
        campaign_id: formData.campaign_id || null,
        category: formData.category,
        display_order: formData.display_order || 0,
        media_items: formData.media_items,
        removed_media_ids: removedMediaIds
      };

      await galleryService.updateGalleryItem(id, galleryData);

      setToast({
        open: true,
        message: 'Gallery item updated successfully!',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/admin/gallery');
      }, 1500);
    } catch (error) {
      console.error('Error saving item:', error);
      setToast({
        open: true,
        message: 'Error saving item: ' + (error.response?.data?.error || error.message),
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/gallery');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={handleCancel}
            sx={{ color: '#2c5f2d', mb: 2, textTransform: 'none' }}
          >
            Back to Gallery
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            Edit Gallery Item
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            Update gallery item details
          </Typography>
        </Box>

        <Card sx={{ p: 4, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={3}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                placeholder="Enter gallery item title"
                variant="outlined"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Enter description"
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>

            {/* Campaign */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Campaign"
                name="campaign_id"
                value={formData.campaign_id}
                onChange={handleFormChange}
                SelectProps={{ native: true }}
                variant="outlined"
              >
                <option value="">-- Select Campaign --</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>{campaign.title}</option>
                ))}
              </TextField>
            </Grid>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                placeholder="e.g., Event, Workshop, Impact"
                variant="outlined"
              />
            </Grid>

            {/* Display Order */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Order"
                name="display_order"
                type="number"
                value={formData.display_order}
                onChange={handleFormChange}
                variant="outlined"
              />
            </Grid>

            {/* Media Input Type Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: '600', color: '#1a202c' }}>
                Add More Media
              </Typography>
              <RadioGroup 
                row 
                value={mediaInputType} 
                onChange={(e) => setMediaInputType(e.target.value)}
              >
                <FormControlLabel value="file" control={<Radio />} label="Upload Images" />
                <FormControlLabel value="url" control={<Radio />} label="Add Video URL" />
              </RadioGroup>
            </Grid>

            {/* File Upload for Images */}
            {mediaInputType === 'file' && (
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '2px dashed #2c5f2d',
                  borderRadius: '8px',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f0f7ff',
                  transition: 'all 0.3s',
                  '&:hover': { backgroundColor: '#e6f2ff' }
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    multiple
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <CloudUpload sx={{ fontSize: 40, color: '#2c5f2d', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#2c5f2d', fontWeight: '600' }}>
                      Click to upload more images
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#667085' }}>
                      JPEG, PNG, GIF, WebP (Max 5MB each)
                    </Typography>
                  </label>
                </Box>
              </Grid>
            )}

            {/* Add Video URL Button */}
            {mediaInputType === 'url' && (
              <Grid item xs={12}>
                <Button 
                  variant="outlined"
                  startIcon={<VideoLibrary />}
                  onClick={handleOpenVideoDialog}
                  sx={{ 
                    color: '#2c5f2d',
                    borderColor: '#2c5f2d',
                    '&:hover': { borderColor: '#1f4620', backgroundColor: '#f0f7ff' }
                  }}
                >
                  Add Video URLs
                </Button>
              </Grid>
            )}

            {/* Media Items Display */}
            {formData.media_items.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: '600', color: '#1a202c' }}>
                  Current Media Items ({formData.media_items.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {formData.media_items.map((media, index) => (
                    <Card key={index} sx={{ p: 2, minWidth: '200px', position: 'relative' }}>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {media.media_type === 'image' ? (
                          <Image sx={{ color: '#2c5f2d', fontSize: 20 }} />
                        ) : (
                          <VideoLibrary sx={{ color: '#7c3aed', fontSize: 20 }} />
                        )}
                        <Chip 
                          label={media.media_type.toUpperCase()}
                          size="small"
                          color={media.media_type === 'image' ? 'primary' : 'secondary'}
                        />
                      </Box>
                      {media.media_type === 'image' && (
                        <img 
                          src={getAssetUrl(media.media_url)} 
                          alt="preview" 
                          style={{
                            width: '100%',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}
                        />
                      )}
                      {media.media_type === 'video' && (
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          mb: 1,
                          wordBreak: 'break-word',
                          maxHeight: '60px',
                          overflow: 'hidden'
                        }}>
                          {media.media_url}
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMedia(index, media.id)}
                        sx={{ position: 'absolute', top: 4, right: 4 }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Card>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                <Button 
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{ 
                    color: '#667085',
                    borderColor: '#d0d5dd',
                    '&:hover': { backgroundColor: '#f8fafb' }
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saving}
                  sx={{ 
                    backgroundColor: '#2c5f2d',
                    '&:hover': { backgroundColor: '#1f4620' }
                  }}
                >
                  {saving ? 'Updating...' : 'Update Item'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Video URL Dialog */}
        <Dialog open={videoDialogOpen} onClose={handleCloseVideoDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a202c', pb: 1 }}>
            Add Video URLs
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="caption" sx={{ color: '#667085', display: 'block', mb: 2 }}>
              Enter one or more video URLs (YouTube, Vimeo, etc.)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {videoUrls.map((url, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    placeholder="https://www.youtube.com/embed/..."
                    value={url}
                    onChange={(e) => handleVideoUrlChange(index, e.target.value)}
                    size="small"
                    variant="outlined"
                  />
                  {videoUrls.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveVideoField(index)}
                      sx={{ mt: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddVideoField}
              size="small"
              sx={{ mt: 2, color: '#2c5f2d', borderColor: '#2c5f2d' }}
            >
              Add Another URL
            </Button>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseVideoDialog} sx={{ color: '#667085' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitVideoUrls}
              variant="contained"
              sx={{ backgroundColor: '#2c5f2d' }}
            >
              Add Videos
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.type}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default GalleryEditPage;
